import i18next from '../../../in18/in18';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types.ts';
import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';
import { SupportDetails } from '@Components/supportDetails/SupportDetails';
import { Icon, Icons } from '@Components/icon/Icon.tsx';

const personalDetailsRoutes = [
  RoutesMap.WELCOME,
  RoutesMap.PHONE,
  RoutesMap.HOME_ADDRESS,
  RoutesMap.IDENTITY,
] as const;
const businessDetailsRoutes = [
  RoutesMap.BUSINESS_TYPE_SELECTION,
  RoutesMap.BUSINESS_ADDRESS,
  RoutesMap.KNOW_YOUR_BUSINESS,
  RoutesMap.INDUSTRY,
  RoutesMap.OWNER_CENTER,
  RoutesMap.ADD_OWNER,
  RoutesMap.ACCOUNT_OPENING_REASON,
] as const;
const planRoutes = [RoutesMap.PLAN_SELECTION] as const;
const confirmYourDetailsRoutes = [RoutesMap.REVIEW] as const;

export interface IPasswordlessDesktopSideBarItem {
  name: string;
  isActive: boolean;
  isPrevious: boolean;
  number: number;
}

const PasswordlessDesktopSideBarItem = ({
  name,
  isActive,
  isPrevious,
  number,
}: IPasswordlessDesktopSideBarItem) => {
  return (
    <li>
      <div className='flex items-center gap-[8px]'>
        {isPrevious ? (
          <Icon icon={Icons.IcColoredCircleCheckmark} className=' object-cover' />
        ) : (
          <div
            className={`w-[28px] h-[28px] text-center items-center  justify-center rounded-full px-[4px] pt-[2px] text-text-main-primary-inverse text-[16px] ${isActive ? 'bg-surface-brand-action-03' : ' bg-surface-primary-bw-06'}`}>
            {number}
          </div>
        )}

        <h1
          className={`font-metricRSemibold text-[18px] ${isActive ? 'text-text-brand-primary' : isPrevious ? 'text-text-main-tertiary-inverse' : 'text-text-main-tertiary'}`}>
          {name}
        </h1>
      </div>
    </li>
  );
};

const menuMap = [
  {
    name: i18next.t('passwordlessSideMenu.personalDetails'),
    routes: personalDetailsRoutes,
  },
  {
    name: i18next.t('passwordlessSideMenu.businessDetails'),
    routes: businessDetailsRoutes,
  },
  {
    name: i18next.t('passwordlessSideMenu.chooseAPlan'),
    routes: planRoutes,
  },
  {
    name: i18next.t('passwordlessSideMenu.confirmation'),
    routes: confirmYourDetailsRoutes,
  },
] as const satisfies {
  name: string;
  routes: readonly RoutesMap[];
}[];

export const PasswordlessDesktopSideBar = () => {
  const { pathname } = useLocation();
  const shouldShowItems = useMemo(
    () =>
      menuMap.some(({ routes }) => routes.some((route) => pathname === routesModel[route].path)),
    [pathname]
  );
  const activeRouteIndex = useMemo(() => {
    let index = -1;
    menuMap.some((item, idx) => {
      if (item.routes.some((route) => pathname === routesModel[route].path)) {
        index = idx;
        return true;
      }
      return false;
    });
    return index;
  }, [pathname]);

  return (
    <div className='z-50 justify-center px-[32px] pt-[56px] pb-[32px] min-w-[270px] w-[270px] xl:w-[400px] bg-surface-primary-bw-00 border-r border-outline-primary-bw-03 h-full hidden sm:flex'>
      <div className='flex flex-col w-full gap-[40px] h-full justify-between'>
        <ul className='gap-[12px] flex flex-col'>
          {shouldShowItems &&
            menuMap.map((item, index: number) => (
              <PasswordlessDesktopSideBarItem
                isActive={index === activeRouteIndex}
                isPrevious={index < activeRouteIndex}
                name={item.name}
                number={index + 1}
                key={index}
              />
            ))}
        </ul>
        <SupportDetails shouldShowLogo />
      </div>
    </div>
  );
};
